import { LitElement, html, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { RovingTabindexController } from '@/controllers/roving-tabindex.controller'
import { contentTypeToLabel } from '@/utils/content-type-label'
import { formatFileSize } from '@/utils/format-file-size'
import { getTranslation } from '@/i18n/translate-utils'
import { GalleryEvent } from '@/models/gallery-event'
import type { FileModel } from '@/models/gallery.model'
import { getCompactPreviewMode, renderPreviewIcon } from './gallery-preview-icon'
import styles from './gallery-list-view.styles.scss?inline'

/**
 * List view for the GoTo Gallery SPA.
 *
 * Renders files as rows with a checkbox, thumbnail (or icon fallback),
 * file name, and metadata (type · size).
 *
 * Keyboard navigation is managed by `RovingTabindexController`:
 * - `ArrowUp` / `ArrowDown` move row focus.
 * - `Space` emits `file-select` for the active row.
 * - `Enter` emits `file-open` for the active row.
 *
 * @prop files       - Array of files to display.
 * @prop selectedIds - Set of currently selected file IDs.
 *
 * @fires file-open   - `CustomEvent<number>` emitted with the file index on row click or Enter.
 * @fires file-select - `CustomEvent<string>` emitted with the file ID on checkbox change or Space.
 */
export class GalleryListView extends LitElement {
  static readonly tagName = 'gallery-list-view'
  static styles = unsafeCSS(styles)

  @property({ attribute: false }) files: FileModel[] = []
  @property({ attribute: false }) selectedIds: Set<string> = new Set()
  @property({ type: Boolean }) selectionEnabled = true

  @state() private _thumbnailErrorKeys: Set<string> = new Set()

  private _roving = new RovingTabindexController(this, { selector: '.list-row', columns: 1 })

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('keydown', this._handleKeydown)
  }

  override disconnectedCallback(): void {
    this.removeEventListener('keydown', this._handleKeydown)
    super.disconnectedCallback()
  }

  /**
   * Moves focus to the row at the given index.
   * Called by parent components to restore list focus after closing detail view.
   */
  setActiveIndex(index: number): void {
    this._roving.setActiveIndex(index)
  }

  private _handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ' && this.selectionEnabled) {
      event.preventDefault()
      const file = this.files[this._roving.activeIndex]
      if (file) {
        this._onCheckboxChange(file.id)
      }
    } else if (event.key === 'Enter') {
      this._onRowClick(this._roving.activeIndex)
    }
  }

  private _onRowClick(index: number): void {
    this.dispatchEvent(
      new CustomEvent<number>(GalleryEvent.FILE_OPEN, { detail: index, bubbles: true, composed: true }),
    )
  }

  private _onCheckboxChange(id: string): void {
    this.dispatchEvent(new CustomEvent<string>(GalleryEvent.FILE_SELECT, { detail: id, bubbles: true, composed: true }))
  }

  private _getThumbnailKey(file: FileModel): string {
    return `${file.id}::${file.thumbnailUrl}`
  }

  private _onThumbnailError(file: FileModel): void {
    this._thumbnailErrorKeys = new Set(this._thumbnailErrorKeys).add(this._getThumbnailKey(file))
  }

  private _onRowKeydown(event: KeyboardEvent, fileId: string, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      this._onRowClick(index)
      return
    }

    if (this.selectionEnabled && (event.key === ' ' || event.key === 'Spacebar')) {
      event.preventDefault()
      event.stopPropagation()
      this._onCheckboxChange(fileId)
    }
  }

  private _renderRow(file: FileModel, index: number) {
    const isSelected = this.selectedIds.has(file.id)
    const previewMode = getCompactPreviewMode(
      file.contentType,
      Boolean(file.thumbnailUrl),
      this._thumbnailErrorKeys.has(this._getThumbnailKey(file)),
    )
    const thumbnail =
      previewMode === 'image'
        ? html`<div class="row-thumb">
            <img
              class="row-thumb-image"
              src="${file.thumbnailUrl}"
              alt=""
              @error=${() => this._onThumbnailError(file)}
            />
          </div>`
        : html`<div class="row-thumb row-thumb--icon">
            ${renderPreviewIcon(file.contentType, true, previewMode === 'highlighted-icon')}
          </div>`

    return html`
      <div
        class="list-row"
        role="option"
        aria-selected="${isSelected ? 'true' : 'false'}"
        aria-label="${file.name}"
        @click=${() => this._onRowClick(index)}
        @keydown=${(event: KeyboardEvent) => this._onRowKeydown(event, file.id, index)}
      >
        ${this.selectionEnabled
          ? html`<chameleon-checkbox
          class="row-checkbox"
          .checked=${isSelected}
          @click=${(e: Event) => e.stopPropagation()}
          @keydown=${(e: KeyboardEvent) => e.stopPropagation()}
          @change=${(e: Event) => {
            e.stopPropagation()
            this._onCheckboxChange(file.id)
          }}
        ></chameleon-checkbox>`
          : ''}
        ${thumbnail}
        <div class="row-info">
          <span class="row-name">${file.name}</span>
          <span class="row-meta">${contentTypeToLabel(file.contentType)} · ${formatFileSize(file.size)}</span>
        </div>
      </div>
    `
  }

  render() {
    return html`
      <div
        class="list-container"
        role="listbox"
        aria-label="${getTranslation('gallery.list.ariaLabel', 'Gallery files')}"
      >
        ${this.files.map((file, i) => this._renderRow(file, i))}
      </div>
    `
  }
}
